import React, { useState, useEffect } from "react";
import { 
  listTiposTratamiento, 
  createTipoTratamiento, 
  updateTipoTratamiento 
} from "../api/odontologia";
import { showAlertError } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiPlus, FiSearch, FiEdit3, FiFileText, FiList
} from "react-icons/fi";

const TiposTratamientoOdontologia = () => {
  const [loading, setLoading] = useState(true);
  const [tipos, setTipos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Búsqueda
  const [buscar, setBuscar] = useState("");
  
  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    status: 1
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listTiposTratamiento();
      setTipos(res?.data || []);
    } catch (error) {
      console.error("Error al cargar tipos de tratamiento:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (tipo = null) => {
    if (tipo) {
      setForm({ 
        nombre: tipo.nombre || "",
        descripcion: tipo.descripcion || "",
        status: tipo.status !== undefined ? tipo.status : 1
      });
      setCurrentId(tipo.id);
      setEditMode(true);
    } else {
      setForm({ 
        nombre: "",
        descripcion: "",
        status: 1 
      });
      setEditMode(false);
    }
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dataToSend = {
        ...form,
        status: parseInt(form.status)
      };

      if (editMode) {
        await updateTipoTratamiento(currentId, dataToSend);
      } else {
        await createTipoTratamiento(dataToSend);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error("Error al guardar el tipo de tratamiento:", error);
      showAlertError("Error", "No se pudo guardar el tipo de tratamiento.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTipos = tipos.filter(t => 
    t.nombre?.toLowerCase().includes(buscar.toLowerCase()) || 
    t.descripcion?.toLowerCase().includes(buscar.toLowerCase())
  );

  if (loading && tipos.length === 0) return <Loader />;

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto font-sans">
      
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Tipos de Tratamiento</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Categorías generales de tratamientos odontológicos</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="w-full sm:w-auto bg-emerald-600 text-white px-4 py-2.5 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm shadow-emerald-200 font-medium flex items-center justify-center gap-2"
        >
          <FiPlus size={20} /> Nuevo Tipo
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-slate-400" />
          </div>
          <input 
            type="text"
            placeholder="Buscar por nombre o descripción..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-slate-50 focus:bg-white text-sm"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold w-16 text-center">ID</th>
                <th className="px-4 py-3 font-semibold">Nombre</th>
                <th className="px-4 py-3 font-semibold">Descripción</th>
                <th className="px-4 py-3 font-semibold text-center w-24">Estado</th>
                <th className="px-4 py-3 font-semibold text-center w-32 sticky right-0 bg-slate-100 border-l border-slate-200 z-10">Opciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTipos.map((t) => (
                <tr key={t.id} className="hover:bg-emerald-50/50 transition-colors group">
                  <td className="px-4 py-3 text-center text-slate-400 font-mono text-xs">{t.id}</td>
                  <td className="px-4 py-3 font-semibold text-slate-700">{t.nombre}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    <p className="line-clamp-2 max-w-md">{t.descripcion}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${String(t.status) === "1" ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-rose-50 text-rose-600 border-rose-200'}`}>
                      {String(t.status) === "1" ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center sticky right-0 bg-slate-100 group-hover:bg-slate-200 border-l border-slate-200 z-0 transition-colors">
                    <button 
                      onClick={() => handleOpenModal(t)}
                      className="bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-50 hover:text-emerald-600 transition-colors text-xs font-medium"
                    >
                      <FiEdit3 className="inline mr-1" /> Editar
                    </button>
                  </td>
                </tr>
              ))}
              {filteredTipos.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-400 italic">No se encontraron tipos de tratamiento registrados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL PRINCIPAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start sm:items-center z-[100] p-2 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col my-4 sm:my-8 animate-fade-in-up">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
                  <FiList size={20} />
                </div>
                <h3 className="text-xl font-bold text-slate-800">
                  {editMode ? "Actualizar Tipo" : "Registrar Nuevo Tipo"}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar">
              <form onSubmit={handleSubmit} id="tipo-form" className="space-y-6">
                 <div className="space-y-4">
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Nombre de la Categoría</label>
                        <input 
                          type="text" 
                          name="nombre"
                          value={form.nombre}
                          onChange={handleChange}
                          placeholder="Ej. Ortodoncia, Endodoncia..."
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 text-sm"
                          required
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Descripción</label>
                        <textarea 
                          name="descripcion"
                          value={form.descripcion}
                          onChange={handleChange}
                          rows="4"
                          placeholder="Breve descripción del propósito de esta categoría..."
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 text-sm"
                        ></textarea>
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Estado</label>
                        <select
                          name="status"
                          value={form.status}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 text-sm"
                        >
                          <option value="1">Activo</option>
                          <option value="0">Inactivo</option>
                        </select>
                    </div>
                 </div>
              </form>
            </div>

            <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white text-sm"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="tipo-form"
                disabled={submitting}
                className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 text-sm flex items-center justify-center gap-2"
              >
                {submitting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                {editMode ? "Actualizar Tipo" : "Guardar Tipo"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TiposTratamientoOdontologia;
